import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type TouchEvent as ReactTouchEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Group, Div, Cell, Button, Spinner,
  FormItem, Input, Text, Headline, ModalRoot,
  ModalPageHeader, IconButton, Checkbox, Snackbar, Select,
  Alert, SegmentedControl, Search, Caption,
  ActionSheet, ActionSheetItem,
} from '@vkontakte/vkui';
import {
  Icon24Add, Icon24Delete, Icon24Write, Icon24Cancel,
  Icon24ChevronUp, Icon24ChevronDown, Icon24InfoCircleOutline, Icon24Search,
} from '@vkontakte/icons';
import { api } from '../../api/client';
import { CatBadge, CAT_ICON_IDS } from '../../ui/CatBadge';
import { useWeddingStore } from '../../stores/wedding';
import type { Task, TaskCategory, Subtask } from '../../types';
import { DragHandle } from '../../ui/DragHandle';
import { ResizableModalPage, sheetId } from '../../ui/ResizableModalPage';
import { ScrollToTopFab } from '../../ui/ScrollToTopFab';
import { TaskCard } from './TaskCard';
import { CategoryCard } from './CategoryCard';
import { TaskGroupHeader } from './TaskGroupHeader';
import { TimelineStage } from './TimelineStage';
import { TaskStatsPill } from './TaskStatsPill';
import { TaskFiltersSheet } from './TaskFiltersSheet';
import { getTaskStatus, groupByDeadline, type SortMode, type StatusMode } from './taskUtils';
import '../../styles/tasks-premium.css';

type ViewMode = 'timeline' | 'list' | 'category';

function loadViewMode(): ViewMode {
  const raw = localStorage.getItem('colibri_mode_checklist');
  return raw === 'list' || raw === 'category' || raw === 'timeline' ? raw : 'list';
}

function saveViewMode(mode: ViewMode) {
  localStorage.setItem('colibri_mode_checklist', mode);
}

function loadSortMode(): SortMode {
  const raw = localStorage.getItem('colibri_tasks_sort');
  return raw === 'name' || raw === 'date' || raw === 'status' || raw === 'position' || raw === 'category' || raw === 'overdue' ? raw : 'position';
}

function saveSortMode(mode: SortMode) {
  localStorage.setItem('colibri_tasks_sort', mode);
}

function loadStatusMode(): StatusMode {
  const raw = localStorage.getItem('colibri_tasks_status');
  return raw === 'completed' || raw === 'pending' || raw === 'overdue' || raw === 'all' || raw === 'soon' ? raw : 'all';
}

function saveStatusMode(mode: StatusMode) {
  localStorage.setItem('colibri_tasks_status', mode);
}

type FormState = {
  name: string;
  note: string;
  due_date: string;
  category_id: string;
  assigned_to: string;
  is_complete: boolean;
};

const emptyForm: FormState = { name: '', note: '', due_date: '', category_id: '', assigned_to: '', is_complete: false };

type CatCardEntry = {
  key: string;
  category: TaskCategory | null;
  name: string;
  iconId: number | null | undefined;
  total: number;
  done: number;
  tasks: Task[];
};

function IconPicker({ value, onPick }: { value: string; onPick: (v: string) => void }) {
  return (
    <div className="colibri-cat-picker">
      {CAT_ICON_IDS.map((id) => (
        <button
          key={id}
          type="button"
          className={`colibri-cat-picker__btn${value === String(id) ? ' colibri-cat-picker__btn--active' : ''}`}
          aria-label={`Вариант иконки ${id}`}
          aria-pressed={value === String(id)}
          onClick={() => onPick(String(id))}
        >
          <CatBadge iconId={id} size={28} />
        </button>
      ))}
    </div>
  );
}

export default function TasksPage() {
  const navigate = useNavigate();
  const { currentWedding, weddingsLoaded, loadMembers, members } = useWeddingStore();

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [subtasks, setSubtasks] = useState<Record<string, Subtask[]>>({});
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);

  const [view, setView] = useState<ViewMode>(loadViewMode);
  const [sort, setSort] = useState<SortMode>(loadSortMode);
  const [status, setStatus] = useState<StatusMode>(loadStatusMode);
  const [filterCat, setFilterCat] = useState('');
  const [q, setQ] = useState('');

  const [modal, setModal] = useState<'create' | 'edit' | 'categories' | 'quick-due' | 'filters' | null>(null);
  const [editing, setEditing] = useState<Task | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const [menuTask, setMenuTask] = useState<Task | null>(null);
  const [menuScreen, setMenuScreen] = useState<'main' | 'cat'>('main');
  const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
  const [catMenu, setCatMenu] = useState<CatCardEntry | null>(null);
  const [catMenuAnchor, setCatMenuAnchor] = useState<Element | null>(null);
  const [expandedCatId, setExpandedCatId] = useState<string | null>(null);
  const [quickDue, setQuickDue] = useState<Task | null>(null);
  const [quickDueValue, setQuickDueValue] = useState('');

  const [newSubtask, setNewSubtask] = useState('');
  const [newSubtaskNote, setNewSubtaskNote] = useState('');

  const [newCat, setNewCat] = useState('');
  const [newCatIcon, setNewCatIcon] = useState('8');
  const [editCat, setEditCat] = useState<TaskCategory | null>(null);
  const [editCatForm, setEditCatForm] = useState({ name: '', note: '', icon_id: '8' });
  const [saving, setSaving] = useState(false);

  const [openToken, setOpenToken] = useState(0);
  const [snack, setSnack] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Task | null>(null);

  /** Global (unfiltered) counters — must not change when status/sort filters are applied. */
  const [stats, setStats] = useState({ done: 0, total: 0, overdue: 0 });

  const isFirstLoad = useRef(true);
  const requestId = useRef(0);
  const longPressTimer = useRef<number | null>(null);
  const pendingPress = useRef<{ taskId: string; startY: number } | null>(null);
  const dragWorkRef = useRef<Task[] | null>(null);
  const dragOriginalRef = useRef<Task[] | null>(null);
  const suppressClickRef = useRef(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const loadAll = useCallback(async (silent = false) => {
    if (!currentWedding) return;
    const rid = ++requestId.current;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('sort', sort === 'category' || sort === 'overdue' ? 'position' : sort);
      if (status !== 'all' && status !== 'soon') params.set('status', status);
      params.set('limit', '1000');
      const [tasksRes, cats, agg] = await Promise.all([
        api.get<{ items: Task[]; total: number }>(`weddings/${currentWedding.id}/tasks?${params.toString()}`),
        api.get<TaskCategory[]>(`weddings/${currentWedding.id}/tasks/categories`),
        api.get<{ total: number; done: number; overdue: number }>(`weddings/${currentWedding.id}/analytics/tasks`),
      ]);
      if (rid !== requestId.current) return; // a newer request already won
      setTasks(tasksRes.items);
      setCategories(cats);
      setStats({ done: agg.done, total: agg.total, overdue: agg.overdue });
    } catch {
      if (rid === requestId.current) setSnack('Ошибка загрузки задач');
    } finally {
      if (rid === requestId.current && !silent) setLoading(false);
    }
  }, [currentWedding, sort, status]);

  /** Keep the stats pill in sync after local mutations (create/edit/toggle/delete). */
  const refreshStats = useCallback(async () => {
    if (!currentWedding) return;
    try {
      const agg = await api.get<{ total: number; done: number; overdue: number }>(`weddings/${currentWedding.id}/analytics/tasks`);
      setStats({ done: agg.done, total: agg.total, overdue: agg.overdue });
    } catch {
      /* keep last known stats */
    }
  }, [currentWedding]);

  useEffect(() => {
    if (!currentWedding) {
      if (weddingsLoaded) navigate('/');
      return;
    }
    // a new wedding must not inherit the previous one's filters/drafts
    setFilterCat('');
    setQ('');
    setExpandedTaskId(null);
    setSubtasks({});
    isFirstLoad.current = true;
    void loadAll();
    void loadMembers();
    // loadAll is recreated on sort/status change — only re-run on wedding switch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWedding, weddingsLoaded]);

  // silent refresh when the user changes sort/status
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    if (!currentWedding) return;
    void loadAll(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, status]);

  const catById = useMemo(() => {
    const m = new Map<string, TaskCategory>();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  const catPos = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of categories) m.set(c.id, c.position);
    return m;
  }, [categories]);

  /** Client-side: search, category chip, 'soon' status, 'category'/'overdue' sorts. Server handles the rest. */
  const filtered = useMemo(() => {
    let list = tasks;
    if (filterCat === 'none') list = list.filter((t) => !t.category_id);
    else if (filterCat) list = list.filter((t) => t.category_id === filterCat);
    if (status === 'soon') list = list.filter((t) => getTaskStatus(t, currentWedding?.timezone) === 'soon');
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((t) => t.name.toLowerCase().includes(needle));
    }
    if (sort === 'category') {
      list = [...list].sort((a, b) => {
        const ca = a.category_id ? catById.get(a.category_id) : undefined;
        const cb = b.category_id ? catById.get(b.category_id) : undefined;
        const pa = ca ? (catPos.get(ca.id) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
        const pb = cb ? (catPos.get(cb.id) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER;
        if (pa !== pb) return pa - pb;
        return (ca ? ca.name : 'яяя').localeCompare(cb ? cb.name : 'яяя', 'ru');
      });
    } else if (sort === 'overdue') {
      list = [...list].sort(
        (a, b) =>
          Number(getTaskStatus(b, currentWedding?.timezone) === 'overdue') -
          Number(getTaskStatus(a, currentWedding?.timezone) === 'overdue'),
      );
    }
    return list;
  }, [tasks, filterCat, q, status, sort, currentWedding?.timezone, catById, catPos]);

  const catCards = useMemo<CatCardEntry[]>(() => {
    if (view !== 'category') return [];
    const map = new Map<string, Task[]>();
    for (const t of filtered) {
      const key = t.category_id || 'none';
      const arr = map.get(key);
      if (arr) arr.push(t);
      else map.set(key, [t]);
    }
    const known = new Set(categories.map((c) => c.id));
    const uncat = [...(map.get('none') || [])];
    for (const [key, list] of map) {
      if (key !== 'none' && !known.has(key)) uncat.push(...list);
    }
    const out: CatCardEntry[] = [...categories]
      .sort((a, b) => (a.position - b.position) || a.name.localeCompare(b.name, 'ru'))
      .map((c) => {
        const tasks = map.get(c.id) || [];
        return {
          key: c.id,
          category: c,
          name: c.name,
          iconId: c.icon_id,
          total: tasks.length,
          done: tasks.filter((t) => t.is_complete).length,
          tasks,
        };
      });
    if (uncat.length) {
      out.push({
        key: 'none',
        category: null,
        name: 'Без категории',
        iconId: null,
        total: uncat.length,
        done: uncat.filter((t) => t.is_complete).length,
        tasks: uncat,
      });
    }
    return out;
  }, [filtered, view, categories]);

  const listGroups = useMemo(() => {
    return groupByDeadline(filtered, currentWedding?.timezone);
  }, [filtered, currentWedding?.timezone]);

  const canReorder = view === 'list' && sort === 'position' && status === 'all' && !q.trim() && !filterCat;
  const activeFilters = (status !== 'all' ? 1 : 0) + (sort !== 'position' ? 1 : 0) + (filterCat ? 1 : 0);

  async function openSubtasks(task: Task) {
    try {
      const list = await api.get<Subtask[]>(`weddings/${currentWedding!.id}/tasks/${task.id}/subtasks`);
      setSubtasks((s) => ({ ...s, [task.id]: list }));
    } catch {
      setSnack('Не удалось загрузить подзадачи');
    }
  }

  async function toggleTask(task: Task) {
    if (!currentWedding) return;
    try {
      const updated = await api.patch<Task>(`weddings/${currentWedding.id}/tasks/${task.id}`, {
        is_complete: !task.is_complete,
      });
      setTasks((ts) => ts.map((t) => (t.id === task.id ? updated : t)));
      void refreshStats();
    } catch {
      setSnack('Не удалось обновить');
    }
  }

  async function handleSave() {
    if (!currentWedding || !form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        note: form.note.trim() || undefined,
        due_date: form.due_date || undefined,
        category_id: form.category_id || undefined,
        assigned_to: form.assigned_to || undefined,
      };
      if (editing) {
        const updated = await api.patch<Task>(`weddings/${currentWedding.id}/tasks/${editing.id}`, {
          ...payload,
          is_complete: form.is_complete,
        });
        setTasks((ts) => ts.map((t) => (t.id === editing.id ? updated : t)));
        setSnack('Задача обновлена');
      } else {
        const created = await api.post<Task>(`weddings/${currentWedding.id}/tasks`, payload);
        if (form.is_complete) {
          const done = await api.patch<Task>(`weddings/${currentWedding.id}/tasks/${created.id}`, { is_complete: true });
          setTasks((ts) => [...ts, done]);
        } else {
          setTasks((ts) => [...ts, created]);
        }
        setSnack('Задача создана');
      }
      void refreshStats();
      setModal(null);
      setEditing(null);
      setForm(emptyForm);
    } catch {
      setSnack('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(task: Task) {
    if (!currentWedding) return;
    try {
      await api.delete(`weddings/${currentWedding.id}/tasks/${task.id}`);
      setTasks((ts) => ts.filter((t) => t.id !== task.id));
      setSnack('Задача удалена');
      void refreshStats();
    } catch {
      setSnack('Ошибка удаления');
    } finally {
      setConfirmDelete(null);
    }
  }

  async function addSubtask(taskId: string) {
    if (!currentWedding || !newSubtask.trim()) return;
    try {
      const s = await api.post<Subtask>(
        `weddings/${currentWedding.id}/tasks/${taskId}/subtasks`,
        { name: newSubtask.trim(), note: newSubtaskNote.trim() || undefined },
      );
      setSubtasks((prev) => ({
        ...prev,
        [taskId]: [...(prev[taskId] || []), s],
      }));
      setNewSubtask('');
      setNewSubtaskNote('');
    } catch {
      setSnack('Не удалось добавить');
    }
  }

  async function toggleSubtask(taskId: string, s: Subtask) {
    if (!currentWedding) return;
    try {
      const updated = await api.patch<Subtask>(
        `weddings/${currentWedding.id}/tasks/${taskId}/subtasks/${s.id}`,
        { is_complete: !s.is_complete },
      );
      setSubtasks((prev) => ({
        ...prev,
        [taskId]: (prev[taskId] || []).map((x) => (x.id === s.id ? updated : x)),
      }));
    } catch {
      setSnack('Ошибка');
    }
  }

  async function deleteSubtask(taskId: string, s: Subtask) {
    if (!currentWedding) return;
    try {
      await api.delete(`weddings/${currentWedding.id}/tasks/${taskId}/subtasks/${s.id}`);
      setSubtasks((prev) => ({
        ...prev,
        [taskId]: (prev[taskId] || []).filter((x) => x.id !== s.id),
      }));
    } catch {
      setSnack('Ошибка удаления');
    }
  }

  async function addCategory() {
    if (!currentWedding || !newCat.trim()) return;
    try {
      const c = await api.post<TaskCategory>(`weddings/${currentWedding.id}/tasks/categories`, {
        name: newCat.trim(),
        icon_id: Number(newCatIcon) || 8,
      });
      setCategories((cs) => [...cs, c].sort((a, b) => (a.position - b.position) || a.name.localeCompare(b.name, 'ru')));
      setNewCat('');
    } catch {
      setSnack('Не удалось создать категорию');
    }
  }

  async function saveCategory() {
    if (!currentWedding || !editCat || !editCatForm.name.trim()) return;
    try {
      const updated = await api.patch<TaskCategory>(
        `weddings/${currentWedding.id}/tasks/categories/${editCat.id}`,
        { name: editCatForm.name.trim(), note: editCatForm.note.trim() || undefined, icon_id: Number(editCatForm.icon_id) || 8 },
      );
      setCategories((cs) => cs.map((x) => (x.id === updated.id ? updated : x)).sort((a, b) => (a.position - b.position) || a.name.localeCompare(b.name, 'ru')));
      setEditCat(null);
    } catch {
      setSnack('Не удалось сохранить');
    }
  }

  async function seedCategories() {
    if (!currentWedding) return;
    try {
      await api.post(`weddings/${currentWedding.id}/tasks/categories/seed`);
      const cats = await api.get<TaskCategory[]>(`weddings/${currentWedding.id}/tasks/categories`);
      setCategories(cats);
      setSnack('Пресеты категорий добавлены');
    } catch {
      setSnack('Не удалось добавить пресеты');
    }
  }

  async function deleteCategory(c: TaskCategory) {
    if (!currentWedding) return;
    try {
      await api.delete(`weddings/${currentWedding.id}/tasks/categories/${c.id}`);
      setCategories((cs) => cs.filter((x) => x.id !== c.id));
      if (filterCat === c.id) setFilterCat('');
    } catch {
      setSnack('Не удалось удалить категорию');
    }
  }

  async function moveCategory(idx: number, dir: -1 | 1) {
    if (!currentWedding) return;
    const gi = idx + dir;
    if (gi < 0 || gi >= categories.length) return;
    const next = [...categories];
    [next[idx], next[gi]] = [next[gi], next[idx]];
    setCategories(next);
    try {
      await api.post(`weddings/${currentWedding.id}/tasks/categories/reorder`, {
        items: next.map((c, i) => ({ id: c.id, position: i + 1 })),
      });
    } catch {
      setSnack('Не удалось изменить порядок');
    }
  }

  function openCreate(categoryId?: string) {
    setEditing(null);
    setForm({ ...emptyForm, category_id: categoryId || '' });
    setOpenToken((t) => t + 1);
    setModal('create');
  }

  function openEdit(t: Task) {
    setEditing(t);
    setForm({
      name: t.name,
      note: t.note || '',
      due_date: t.due_date || '',
      category_id: t.category_id || '',
      assigned_to: t.assigned_to || '',
      is_complete: t.is_complete,
    });
    setOpenToken((t) => t + 1);
    setModal('edit');
  }

  function openQuickDue(t: Task) {
    setQuickDue(t);
    setQuickDueValue(t.due_date || '');
    setOpenToken((tok) => tok + 1);
    setModal('quick-due');
    setMenuTask(null);
    setMenuScreen('main');
  }

  function openEditCategory(c: TaskCategory) {
    setEditCat(c);
    setEditCatForm({ name: c.name, note: c.note || '', icon_id: String(c.icon_id || 8) });
    setOpenToken((t) => t + 1);
    setModal('categories');
    setCatMenu(null);
    setCatMenuAnchor(null);
  }

  async function quickPatch(task: Task, patch: { category_id?: string | null; due_date?: string | null }) {
    if (!currentWedding) return;
    try {
      const updated = await api.patch<Task>(`weddings/${currentWedding.id}/tasks/${task.id}`, patch);
      setTasks((ts) => ts.map((t) => (t.id === task.id ? updated : t)));
      setSnack('Задача обновлена');
      void refreshStats();
    } catch {
      setSnack('Не удалось обновить');
    } finally {
      setMenuTask(null);
      setMenuScreen('main');
      setQuickDue(null);
    }
  }

  function commitOrder(work: Task[]) {
    const order = work.map((x, i) => ({ id: x.id, position: i + 1 }));
    const posById = new Map(order.map((o) => [o.id, o.position]));
    // BUGFIX: re-sort the array itself — .map() alone kept the old visual order.
    setTasks((ts) => ts
      .map((x) => (posById.has(x.id) ? { ...x, position: posById.get(x.id)! } : x))
      .sort((x, y) => (x.position ?? 0) - (y.position ?? 0)));
    if (!currentWedding) return;
    api.post(`weddings/${currentWedding.id}/tasks/reorder`, { items: order }).catch(() => {
      setSnack('Не удалось изменить порядок');
    });
  }

  function pressStart(e: ReactTouchEvent<HTMLDivElement> | ReactMouseEvent<HTMLDivElement>, t: Task) {
    if (!canReorder) return;
    if ((e.target as HTMLElement).closest('button, input, a')) return;
    const y = 'touches' in e ? e.touches[0].clientY : e.clientY;
    pendingPress.current = { taskId: t.id, startY: y };
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = window.setTimeout(() => {
      longPressTimer.current = null;
      if (!pendingPress.current || pendingPress.current.taskId !== t.id) return;
      dragWorkRef.current = [...tasks].sort((x, y) => (x.position ?? 0) - (y.position ?? 0) || x.name.localeCompare(y.name, 'ru'));
      dragOriginalRef.current = tasks;
      suppressClickRef.current = true;
      setDraggingId(t.id);
    }, 250);
  }

  function pressMove(e: ReactTouchEvent<HTMLDivElement> | ReactMouseEvent<HTMLDivElement>) {
    if (draggingId || !pendingPress.current) return;
    const y = 'touches' in e ? e.touches[0].clientY : e.clientY;
    if (Math.abs(y - pendingPress.current.startY) > 10) {
      if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      pendingPress.current = null;
    }
  }

  function pressEnd() {
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
    pendingPress.current = null;
  }

  function endDrag(commit: boolean) {
    const work = dragWorkRef.current;
    const original = dragOriginalRef.current;
    dragWorkRef.current = null;
    dragOriginalRef.current = null;
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
    setDraggingId(null);
    setOverId(null);
    if (!work) return;
    if (commit) commitOrder(work);
    else if (original) setTasks(original);
    window.setTimeout(() => { suppressClickRef.current = false; }, 300);
  }

  useEffect(() => {
    if (!draggingId) return;
    const onMove = (e: TouchEvent | MouseEvent) => {
      e.preventDefault();
      const y = 'touches' in e && e.touches.length ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const el = document.elementFromPoint(window.innerWidth / 2, y) as HTMLElement | null;
      const row = el?.closest('[data-task-id]') as HTMLElement | null;
      const overTask = row?.dataset.taskId || null;
      if (!overTask || overTask === draggingId) return;
      setOverId(overTask);
      const work = dragWorkRef.current;
      if (!work) return;
      const from = work.findIndex((x) => x.id === draggingId);
      const to = work.findIndex((x) => x.id === overTask);
      if (from < 0 || to < 0 || from === to) return;
      const [moved] = work.splice(from, 1);
      work.splice(to, 0, moved);
      const order = work.map((x, i) => ({ id: x.id, position: i + 1 }));
      const posById = new Map(order.map((o) => [o.id, o.position]));
      setTasks((ts) => ts
        .map((x) => (posById.has(x.id) ? { ...x, position: posById.get(x.id)! } : x))
        .sort((x, y) => (x.position ?? 0) - (y.position ?? 0)));
    };
    const onEnd = () => endDrag(true);
    const onCancel = () => endDrag(false);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onCancel);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingId]);

  function memberName(id?: string) {
    if (!id) return '';
    const m = members.find((x) => x.id === id);
    if (!m) return '';
    return [m.user_first_name, m.user_last_name].filter(Boolean).join(' ') || 'Участник';
  }

  function toggleExpand(t: Task) {
    if (expandedTaskId === t.id) {
      setExpandedTaskId(null);
    } else {
      // BUGFIX: drop the previous task's draft so it cannot leak into another task
      setNewSubtask('');
      setNewSubtaskNote('');
      setExpandedTaskId(t.id);
      void openSubtasks(t);
    }
  }

  function renderTask(t: Task) {
    const subs = subtasks[t.id];
    const expanded = expandedTaskId === t.id;
    const assignee = memberName(t.assigned_to);
    const cat = t.category_id ? catById.get(t.category_id) : undefined;
    const status = getTaskStatus(t, currentWedding?.timezone);
    return (
      <div key={t.id}>
        <TaskCard
          task={t}
          category={cat}
          status={status}
          assignee={assignee || undefined}
          subtasks={subs ? { done: subs.filter((s) => s.is_complete).length, total: subs.length } : null}
          attachmentCount={t.attachment_count || null}
          expanded={expanded}
          dragging={draggingId === t.id}
          isOver={overId === t.id && !!draggingId && draggingId !== t.id}
          onToggle={() => void toggleTask(t)}
          onOpen={() => { if (suppressClickRef.current) return; navigate(`/tasks/${t.id}`); }}
          onMenu={(el) => { setMenuAnchor(el); setMenuTask(t); setMenuScreen('main'); }}
          onExpand={() => toggleExpand(t)}
          dragAttrs={{
            onTouchStart: (e) => pressStart(e, t),
            onTouchMove: pressMove,
            onTouchEnd: pressEnd,
            onMouseDown: (e) => pressStart(e, t),
            onMouseMove: pressMove,
            onMouseUp: pressEnd,
          }}
        />

        {expanded && (
          <Div style={{ background: 'var(--vkui--color_background_secondary)', paddingTop: 8, paddingBottom: 8 }}>
            {subs === undefined ? (
              <Spinner size="s" />
            ) : subs.length === 0 ? (
              <Caption style={{ color: 'var(--vkui--color_text_secondary)' }}>Нет подзадач</Caption>
            ) : (
              subs.map((s) => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <Checkbox checked={s.is_complete} onChange={() => void toggleSubtask(t.id, s)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.is_complete ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>{s.name}</div>
                    {s.note ? (
                      <div style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>{s.note}</div>
                    ) : null}
                  </div>
                  <IconButton label="Удалить" onClick={() => void deleteSubtask(t.id, s)}>
                    <Icon24Delete />
                  </IconButton>
                </div>
              ))
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <Input
                value={expanded ? newSubtask : ''}
                onChange={(e) => setNewSubtask(e.target.value)}
                placeholder="Подзадача"
                style={{ flex: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void addSubtask(t.id);
                }}
              />
              <Input
                value={expanded ? newSubtaskNote : ''}
                onChange={(e) => setNewSubtaskNote(e.target.value)}
                placeholder="Заметка"
                style={{ flex: 1 }}
              />
              <Button size="s" onClick={() => void addSubtask(t.id)} disabled={!newSubtask.trim()}>+</Button>
            </div>
          </Div>
        )}
      </div>
    );
  }

  return (
    <Panel className="tp-root">
      <PanelHeader
        before={<PanelHeaderBack onClick={() => navigate('/')} />}
        after={
          <IconButton label="Аналитика чек-листа" onClick={() => navigate('/tasks/analytics')}>
            <Icon24InfoCircleOutline />
          </IconButton>
        }
      >
        Задачи
      </PanelHeader>

      <Group>
        <Div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <TaskStatsPill
            weddingDate={currentWedding?.wedding_date}
            stats={stats}
            shown={filtered.length}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="m"
              mode="secondary"
              style={{ flex: 1 }}
              onClick={() => { setOpenToken((t) => t + 1); setModal('filters'); }}
            >
              {activeFilters > 0 ? `Фильтры · ${activeFilters}` : 'Фильтры'}
            </Button>
            <Button size="m" mode="primary" style={{ flex: 1 }} before={<Icon24Add />} onClick={() => openCreate()}>
              Новая задача
            </Button>
          </div>
        </Div>

        <Div style={{ paddingTop: 0, paddingBottom: 8 }}>
          <SegmentedControl
            value={view}
            onChange={(v) => {
              const mode = v as ViewMode;
              setView(mode);
              saveViewMode(mode);
            }}
            options={[
              { label: 'Список', value: 'list' },
              { label: 'Таймлайн', value: 'timeline' },
              { label: 'Категории', value: 'category' },
            ]}
          />
        </Div>

        <Div style={{ paddingTop: 0, paddingBottom: 8 }}>
          <Search
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск по задачам"
            after={<Icon24Search />}
          />
        </Div>

        {categories.length > 0 && (
          <Div style={{ paddingTop: 0, paddingBottom: 8 }}>
            <div className="colibri-chip-scroll">
              <Button
                size="s"
                mode={filterCat === '' ? 'primary' : 'secondary'}
                onClick={() => setFilterCat('')}
                style={{ flexShrink: 0 }}
              >
                Все
              </Button>
              {categories.map((c) => (
                <Button
                  key={c.id}
                  size="s"
                  mode={filterCat === c.id ? 'primary' : 'secondary'}
                  onClick={() => setFilterCat(filterCat === c.id ? '' : c.id)}
                  before={<CatBadge iconId={c.icon_id} size={20} />}
                  style={{ flexShrink: 0 }}
                >
                  {c.name}
                </Button>
              ))}
              <Button
                size="s"
                mode={filterCat === 'none' ? 'primary' : 'secondary'}
                onClick={() => setFilterCat(filterCat === 'none' ? '' : 'none')}
                style={{ flexShrink: 0 }}
              >
                Без категории
              </Button>
              <Button size="s" mode="secondary" onClick={() => { setOpenToken((t) => t + 1); setModal('categories'); }} style={{ flexShrink: 0 }}>
                + Категория
              </Button>
            </div>
          </Div>
        )}
      </Group>

      {loading ? (
        <Group>
          <Div style={{ textAlign: 'center', padding: 40 }}><Spinner size="l" /></Div>
        </Group>
      ) : filtered.length === 0 ? (
        <Group>
          <Div style={{ textAlign: 'center', padding: 32 }}>
            <Headline level="2">
              {q.trim() ? 'Задачи не найдены' : status === 'overdue' ? 'Просроченных нет' : 'Нет задач'}
            </Headline>
            <Text style={{ marginTop: 8, color: 'var(--vkui--color_text_secondary)' }}>
              {q.trim()
                ? 'Измените запрос поиска'
                : 'Добавьте первую задачу чек-листа — так вы ничего не забудете'}
            </Text>
            {!q.trim() && (
              <Button size="m" mode="secondary" stretched style={{ marginTop: 16 }} onClick={() => openCreate()}>
                Добавить задачу
              </Button>
            )}
          </Div>
        </Group>
      ) : view === 'timeline' ? (
        <>
          {!currentWedding?.wedding_date && (
            <Group>
              <Div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Text style={{ flex: 1, minWidth: 180, color: 'var(--vkui--color_text_secondary)' }}>
                  Задайте дату свадьбы — задачи распределятся по таймлайну
                </Text>
                <Button size="m" mode="secondary" onClick={() => navigate('/settings')}>
                  Задать дату
                </Button>
              </Div>
            </Group>
          )}
          {(listGroups || []).map(([label, list]) => (
            <TimelineStage key={label} label={label} count={list.length}>
              {list.map((t) => renderTask(t))}
            </TimelineStage>
          ))}
        </>
      ) : view === 'category' ? (
        catCards.map((cc) => (
          <Group key={cc.key}>
            <CategoryCard
              category={cc.category}
              name={cc.name}
              iconId={cc.iconId}
              total={cc.total}
              done={cc.done}
              expanded={expandedCatId === cc.key}
              onToggle={() => setExpandedCatId(expandedCatId === cc.key ? null : cc.key)}
              onMenu={(el) => { setCatMenuAnchor(el); setCatMenu(cc); }}
            />
            {expandedCatId === cc.key && cc.tasks.map((t) => renderTask(t))}
          </Group>
        ))
      ) : (
        (listGroups || []).map(([label, list]) => (
          <Group key={label}>
            <TaskGroupHeader label={label} count={list.length} />
            {list.map((t) => renderTask(t))}
          </Group>
        ))
      )}

      <ModalRoot
        activeModal={
          modal === 'create' || modal === 'edit'
            ? sheetId('task-form', openToken)
            : modal === 'categories'
              ? sheetId('cat-form', openToken)
              : modal === 'quick-due'
                ? sheetId('quick-due', openToken)
                : modal === 'filters'
                  ? sheetId('filters', openToken)
                  : null
        }
      >
        <ResizableModalPage openToken={openToken} id="task-form" onClose={() => { setModal(null); setEditing(null); }} settlingHeight={80}>
          <ModalPageHeader>{editing ? 'Редактировать' : 'Новая задача'}</ModalPageHeader>
          <DragHandle />
          <Group>
            <FormItem top="Название *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Выбрать место" />
            </FormItem>
            <FormItem top="Срок">
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </FormItem>
            <FormItem top="Категория">
              <Select
                value={form.category_id}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                options={[
                  { label: 'Без категории', value: '' },
                  ...categories.map((c) => ({ label: c.name, value: c.id })),
                ]}
              />
            </FormItem>
            <FormItem top="Ответственный">
              <Select
                value={form.assigned_to}
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                options={[
                  { label: 'Не назначен', value: '' },
                  ...members.map((m) => ({
                    label: [m.user_first_name, m.user_last_name].filter(Boolean).join(' ') || 'Участник',
                    value: m.id,
                  })),
                ]}
              />
            </FormItem>
            <FormItem top="Статус">
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="s"
                  mode={!form.is_complete ? 'primary' : 'secondary'}
                  onClick={() => setForm({ ...form, is_complete: false })}
                >
                  В ожидании
                </Button>
                <Button
                  size="s"
                  mode={form.is_complete ? 'primary' : 'secondary'}
                  onClick={() => setForm({ ...form, is_complete: true })}
                >
                  Выполнено
                </Button>
              </div>
            </FormItem>
            <FormItem top="Заметка">
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </FormItem>
            <Div style={{ display: 'flex', gap: 8 }}>
              <Button size="m" mode="primary" onClick={() => void handleSave()} disabled={!form.name.trim() || saving}>
                Сохранить
              </Button>
              <Button size="m" mode="secondary" onClick={() => { setModal(null); setEditing(null); }}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="cat-form" onClose={() => setModal(null)} settlingHeight={80}>
          <ModalPageHeader>Категории</ModalPageHeader>
          <DragHandle />
          <Group>
            <Div>
              <Button size="s" mode="secondary" onClick={() => void seedCategories()}>
                + Пресеты категорий (11)
              </Button>
            </Div>
            {editCat && (
              <Div style={{ background: 'var(--vkui--color_background_secondary)', borderRadius: 8, margin: 12, padding: 12 }}>
                <FormItem top="Название">
                  <Input
                    value={editCatForm.name}
                    onChange={(e) => setEditCatForm({ ...editCatForm, name: e.target.value })}
                  />
                </FormItem>
                <FormItem top="Заметка">
                  <Input
                    value={editCatForm.note}
                    onChange={(e) => setEditCatForm({ ...editCatForm, note: e.target.value })}
                  />
                </FormItem>
                <FormItem top="Иконка">
                  <IconPicker value={editCatForm.icon_id} onPick={(v) => setEditCatForm({ ...editCatForm, icon_id: v })} />
                </FormItem>
                <Div style={{ display: 'flex', gap: 8, padding: 0 }}>
                  <Button size="s" mode="primary" onClick={() => void saveCategory()} disabled={!editCatForm.name.trim()}>
                    Сохранить
                  </Button>
                  <Button size="s" mode="secondary" onClick={() => setEditCat(null)}>
                    Отмена
                  </Button>
                </Div>
              </Div>
            )}
            <Div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Input
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value)}
                  placeholder="Новая категория"
                  style={{ flex: 1 }}
                />
                <Button size="s" mode="primary" onClick={() => void addCategory()} disabled={!newCat.trim()}>+</Button>
              </div>
              <div style={{ marginTop: 8 }}>
                <IconPicker value={newCatIcon} onPick={setNewCatIcon} />
              </div>
            </Div>
            {categories.length === 0 ? (
              <Div>
                <Caption style={{ color: 'var(--vkui--color_text_secondary)' }}>Нет категорий</Caption>
              </Div>
            ) : (
              categories.map((c, idx) => (
                <Cell
                  key={c.id}
                  before={<CatBadge iconId={c.icon_id} />}
                  after={
                    <div style={{ display: 'flex', gap: 2 }}>
                      <IconButton label="Выше" onClick={() => void moveCategory(idx, -1)} disabled={idx === 0}>
                        <Icon24ChevronUp />
                      </IconButton>
                      <IconButton label="Ниже" onClick={() => void moveCategory(idx, 1)} disabled={idx === categories.length - 1}>
                        <Icon24ChevronDown />
                      </IconButton>
                      <IconButton label="Изменить" onClick={() => {
                        setEditCat(c);
                        setEditCatForm({ name: c.name, note: c.note || '', icon_id: String(c.icon_id || 8) });
                      }}>
                        <Icon24Write />
                      </IconButton>
                      <IconButton label="Удалить" onClick={() => void deleteCategory(c)}>
                        <Icon24Delete />
                      </IconButton>
                    </div>
                  }
                >
                  {c.name}
                </Cell>
              ))
            )}
          </Group>
        </ResizableModalPage>

        <ResizableModalPage openToken={openToken} id="quick-due" onClose={() => { setModal(null); setQuickDue(null); }} settlingHeight={45}>
          <ModalPageHeader>Изменить срок</ModalPageHeader>
          <DragHandle />
          <Group>
            <FormItem top="Срок">
              <Input type="date" value={quickDueValue} onChange={(e) => setQuickDueValue(e.target.value)} />
            </FormItem>
            <Div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="m"
                mode="primary"
                disabled={!quickDue}
                onClick={() => {
                  if (quickDue) void quickPatch(quickDue, { due_date: quickDueValue || null });
                  setModal(null);
                }}
              >
                Сохранить
              </Button>
              <Button size="m" mode="secondary" onClick={() => { setModal(null); setQuickDue(null); }}>
                Отмена
              </Button>
            </Div>
          </Group>
        </ResizableModalPage>

        <TaskFiltersSheet
          key={openToken}
          openToken={openToken}
          status={status}
          sort={sort}
          onApply={(s, so) => {
            setStatus(s);
            saveStatusMode(s);
            setSort(so);
            saveSortMode(so);
          }}
          onReset={() => {
            setStatus('all');
            saveStatusMode('all');
            setSort('position');
            saveSortMode('position');
          }}
          onClose={() => setModal(null)}
        />
      </ModalRoot>

      {catMenu && (
        <ActionSheet
          mode="sheet"
          title={catMenu.name}
          toggleRef={catMenuAnchor}
          onClosed={() => { setCatMenu(null); setCatMenuAnchor(null); }}
          iosCloseItem={<ActionSheetItem mode="cancel">Отмена</ActionSheetItem>}
        >
          <ActionSheetItem onClick={() => openCreate(catMenu.category ? catMenu.category.id : undefined)}>
            Добавить задачу
          </ActionSheetItem>
          <ActionSheetItem
            onClick={() => {
              setFilterCat(catMenu.category ? catMenu.category.id : 'none');
              setView('list');
              saveViewMode('list');
            }}
          >
            Показать в списке
          </ActionSheetItem>
          {catMenu.category && (
            <ActionSheetItem onClick={() => openEditCategory(catMenu.category!)}>Изменить</ActionSheetItem>
          )}
          {catMenu.category && (
            <ActionSheetItem mode="destructive" onClick={() => void deleteCategory(catMenu.category!)}>
              Удалить
            </ActionSheetItem>
          )}
        </ActionSheet>
      )}

      {menuTask && (
        <ActionSheet
          mode="sheet"
          title={menuScreen === 'cat' ? 'Изменить категорию' : menuTask.name}
          toggleRef={menuAnchor}
          onClosed={() => { setMenuTask(null); setMenuScreen('main'); setMenuAnchor(null); }}
          iosCloseItem={<ActionSheetItem mode="cancel">Отмена</ActionSheetItem>}
        >
          {menuScreen === 'main' ? (
            <>
              <ActionSheetItem onClick={() => navigate(`/tasks/${menuTask.id}`)}>Подробнее</ActionSheetItem>
              <ActionSheetItem onClick={() => openEdit(menuTask)}>Редактировать</ActionSheetItem>
              <ActionSheetItem autoCloseDisabled onImmediateClick={() => setMenuScreen('cat')}>Изменить категорию</ActionSheetItem>
              <ActionSheetItem onClick={() => openQuickDue(menuTask)}>Изменить срок</ActionSheetItem>
              <ActionSheetItem mode="destructive" onClick={() => setConfirmDelete(menuTask)}>Удалить</ActionSheetItem>
            </>
          ) : (
            <>
              <ActionSheetItem onClick={() => void quickPatch(menuTask, { category_id: null })}>Без категории</ActionSheetItem>
              {categories.map((c) => (
                <ActionSheetItem key={c.id} onClick={() => void quickPatch(menuTask, { category_id: c.id })}>
                  {c.name}
                </ActionSheetItem>
              ))}
            </>
          )}
        </ActionSheet>
      )}

      {confirmDelete && (
        <Alert
          actions={[
            {
              title: 'Удалить',
              mode: 'destructive',
              autoCloseDisabled: true,
              action: () => {
                const t = confirmDelete;
                setConfirmDelete(null);
                if (t) void handleDelete(t);
              },
            },
            {
              title: 'Отмена',
              mode: 'cancel',
            },
          ]}
          actionsLayout="vertical"
          onClose={() => setConfirmDelete(null)}
          onClosed={() => setConfirmDelete(null)}
          title="Удалить задачу?"
          description={`«${confirmDelete.name}» будет удалена безвозвратно.`}
        />
      )}

      {snack && (
        <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)} before={<Icon24Cancel />}>
          {snack}
        </Snackbar>
      )}
      <ScrollToTopFab />
      <Div style={{ height: 24 }} />
    </Panel>
  );
}
